// =============================================================================
// VINUM - Constants
// Migrated from Flutter config with EXACT values
// =============================================================================

// -----------------------------------------------------------------------------
// API Configuration
// -----------------------------------------------------------------------------

// Get API URL from environment variables (supports both Vite and Expo)
// @ts-ignore - VITE_API_URL is statically replaced by Vite at build time
const __VITE_API_URL__: string | undefined = import.meta.env?.VITE_API_URL;

function getApiUrl(): string {
  // Vite (web) - statically replaced at build time
  if (__VITE_API_URL__) return __VITE_API_URL__;
  // Expo (mobile) - uses process.env with EXPO_PUBLIC_ prefix
  if (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_API_URL) {
    return process.env.EXPO_PUBLIC_API_URL;
  }
  // Default fallback
  return 'http://localhost:4000';
}

export const API_CONFIG = {
  BASE_URL: getApiUrl(),
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

// Get WebSocket URL from the API base URL (http→ws, https→wss)
export function getWsUrl(): string {
  const base = API_CONFIG.BASE_URL;
  return base.replace(/^http/, 'ws') + '/ws';
}

// -----------------------------------------------------------------------------
// Storage Keys
// Exact match with Flutter SharedPreferences/SecureStorage keys
// -----------------------------------------------------------------------------

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  CART_ITEMS: 'cart_items',
  THEME_MODE: 'theme_mode',
  FCM_TOKEN: 'fcm_token',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  LAST_SYNC_TIME: 'last_sync_time',
} as const;

// -----------------------------------------------------------------------------
// Breakpoints
// Exact match with Flutter ResponsiveConfig
// -----------------------------------------------------------------------------

export const BREAKPOINTS = {
  SMALL: 480,    // < 480
  MOBILE: 800,   // 480 - 799
  TABLET: 1000,  // 800 - 999
  DESKTOP: 1000, // >= 1000
} as const;

// Helper functions matching Flutter AppBreakpoints
export const isSmall = (width: number): boolean => width < BREAKPOINTS.SMALL;
export const isMobile = (width: number): boolean => width >= BREAKPOINTS.SMALL && width < BREAKPOINTS.MOBILE;
export const isTablet = (width: number): boolean => width >= BREAKPOINTS.MOBILE && width < BREAKPOINTS.TABLET;
export const isDesktop = (width: number): boolean => width >= BREAKPOINTS.DESKTOP;

export const getBreakpoint = (width: number): 'small' | 'mobile' | 'tablet' | 'desktop' => {
  if (width < BREAKPOINTS.SMALL) return 'small';
  if (width < BREAKPOINTS.MOBILE) return 'mobile';
  if (width < BREAKPOINTS.TABLET) return 'tablet';
  return 'desktop';
};

// -----------------------------------------------------------------------------
// Theme Colors
// Exact match with Flutter AppColors (from app_colors.dart)
// -----------------------------------------------------------------------------

export const COLORS = {
  // Primary colors - Wine red (#AD1122)
  primary: '#AD1122',
  primaryLight: '#E74C5E',
  primaryDark: '#7A0D1B',
  primaryContainer: '#FDD9DD',

  // Secondary colors - Golden (#BC952E)
  secondary: '#BC952E',
  secondaryLight: '#DDB85E',
  secondaryDark: '#8A6A1E',
  secondaryContainer: '#FDF3D9',

  // Neutral palette
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // Light theme - exact Flutter values
  light: {
    background: '#FFFFFF',
    backgroundAlt: '#F9F9F9',
    card: '#FFFFFF',
    cardElevated: '#FAFAFA',
    divider: '#E0E0E0',
    border: '#E0E0E0',
    textPrimary: '#1A1A1A',
    textSecondary: '#666666',
    textDisabled: '#999999',
    textHint: '#BDBDBD',
    overlay: 'rgba(0, 0, 0, 0.54)',
    shadow: 'rgba(0, 0, 0, 0.1)',
    shadowLight: 'rgba(0, 0, 0, 0.05)',
    shadowMedium: 'rgba(0, 0, 0, 0.15)',
  },

  // Dark theme - exact Flutter values
  dark: {
    primary: '#C91A2B',
    primaryLight: '#D64653',
    secondary: '#DDB85E',
    secondaryLight: '#E7C88F',
    background: '#121212',
    backgroundAlt: '#181818',
    card: '#1E1E1E',
    cardElevated: '#2A2A2A',
    divider: '#2E2E2E',
    border: '#3A3A3A',
    textPrimary: '#FFFFFF',
    textSecondary: '#B0B0B0',
    textDisabled: '#757575',
    textHint: '#616161',
    overlay: 'rgba(0, 0, 0, 0.7)',
    shadow: 'rgba(0, 0, 0, 0.2)',
    shadowLight: 'rgba(0, 0, 0, 0.33)',
    shadowMedium: 'rgba(0, 0, 0, 0.4)',
  },

  // Semantic colors - Status (Flutter values)
  success: '#4CAF50',
  successLight: '#81C784',
  successDark: '#388E3C',
  warning: '#FFA726',
  warningLight: '#FFB74D',
  warningDark: '#F57C00',
  error: '#E53935',
  errorLight: '#EF5350',
  errorDark: '#C62828',
  info: '#2196F3',
  infoLight: '#64B5F6',
  infoDark: '#1976D2',

  // Dark mode semantic colors (brighter variants)
  darkMode: {
    success: '#66BB6A',
    warning: '#FFA726',
    error: '#EF5350',
    info: '#64B5F6',
  },

  // Standard
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',

  // Order status colors - aligned with backend States table
  orderStatus: {
    'In Attesa': '#FFA726',
    'Confermato': '#4CAF50',
    'Rifiutato': '#E53935',
  },
} as const;

// -----------------------------------------------------------------------------
// Spacing
// Exact match with Flutter AppSpacing
// -----------------------------------------------------------------------------

export const SPACING = {
  xxxs: 2,
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,

  // Input specific
  inputPaddingHorizontal: 16,
  inputPaddingVertical: 14,

  // Border radius
  radiusXs: 4,
  radiusSm: 8,
  radiusMd: 12,
  radiusLg: 16,
  radiusXl: 24,
  radiusFull: 9999,

  // Page padding by breakpoint
  pagePadding: {
    small: 12,
    mobile: 16,
    tablet: 24,
    desktop: 32,
  },
} as const;

// -----------------------------------------------------------------------------
// Typography
// Exact match with Flutter AppTypography
// -----------------------------------------------------------------------------

export const TYPOGRAPHY = {
  fontFamily: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", monospace',
  },

  fontSize: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 28,
    '4xl': 32,
    '5xl': 40,
  },

  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
} as const;

// -----------------------------------------------------------------------------
// Animation
// Exact match with Flutter animation durations
// -----------------------------------------------------------------------------

export const ANIMATION = {
  durationFast: 150,
  durationNormal: 300,
  durationSlow: 500,
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// -----------------------------------------------------------------------------
// Routes
// Exact match with Flutter AppRoutes
// -----------------------------------------------------------------------------

export const ROUTES = {
  // Auth routes
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  REQUEST_RESET: '/request-reset',
  RESET_PASSWORD_CODE: '/reset-password-code',
  RESET_PASSWORD: '/reset-password',
  AWAIT_APPROVAL: '/await-approval',
  LIMITED_ACCESS: '/limited-access',

  // Main routes
  HOME: '/',
  CATALOG: '/catalog',
  FAVORITES: '/favorites',
  WINE_DETAIL: '/wine/:itemId',
  CART: '/cart',
  CHECKOUT: '/checkout',
  ORDERS: '/orders',
  ORDER_DETAIL: '/orders/:orderId',
  SCARICHI: '/scarichi',
  PROFILE: '/profile',
  EDIT_PROFILE: '/profile/edit',
  ACCOUNT_SETTINGS: '/profile/settings',
  NOTIFICATIONS: '/profile/notifications',
  WINERY: '/winery',
  STATISTICS: '/statistics',
  STATISTICS_PRODUCTS: '/statistics/products',
  STATISTICS_SALES: '/statistics/sales',
  TOKEN_SCREEN: '/token',

  // Admin routes
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_USER_DETAIL: '/admin/users/:userId',
  ADMIN_CONTENT: '/admin/content',
  ADMIN_CONTENT_EDIT: '/admin/content/:contentId',
  ADMIN_CATALOG: '/admin/catalog',
  ADMIN_ORDERS: '/admin/orders',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_ANALYTICS: '/admin/analytics',
  ADMIN_AUDIT: '/admin/audit',
  ADMIN_NOTIFICATIONS: '/admin/notifications',
  ADMIN_WINERIES: '/admin/wineries',
  ADMIN_STOCK_OUTPUTS: '/admin/stock-outputs',
} as const;

// Helper to build dynamic routes
export const buildRoute = {
  wineDetail: (itemId: string) => `/wine/${itemId}`,
  orderDetail: (orderId: string) => `/orders/${orderId}`,
  adminUserDetail: (userId: string) => `/admin/users/${userId}`,
  adminContentEdit: (contentId: string) => `/admin/content/${contentId}`,
};

// -----------------------------------------------------------------------------
// Validation
// Exact match with Flutter validation patterns
// -----------------------------------------------------------------------------

export const VALIDATION = {
  email: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
  password: {
    minLength: 6,
    pattern: /^.{6,}$/,
  },
  phone: /^\+?[0-9]{10,15}$/,
  partitaIVA: /^[0-9]{11}$/,
  codiceFiscale: /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i,
  cap: /^[0-9]{5}$/,
} as const;

// -----------------------------------------------------------------------------
// Pagination
// -----------------------------------------------------------------------------

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;

// -----------------------------------------------------------------------------
// Order Status Labels (Italian)
// Exact match with Flutter
// -----------------------------------------------------------------------------

export const ORDER_STATUS_LABELS: Record<string, string> = {
  'In Attesa': 'In Attesa',
  'Confermato': 'Confermato',
  'Rifiutato': 'Rifiutato',
  all: 'Tutti',
} as const;

// -----------------------------------------------------------------------------
// Account Types
// -----------------------------------------------------------------------------

export const ACCOUNT_TYPES = {
  PRIVATE: 'Privato',
  COMPANY: 'Azienda',
} as const;

// -----------------------------------------------------------------------------
// User Roles
// -----------------------------------------------------------------------------

export const USER_ROLES = {
  USER: 'User',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
} as const;
